import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { CLAIM_STATUS_LABELS } from '@/components/ClaimsTable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ClaimStatus, type InternalClaim, useSetClaimStatusNote } from '@/models/claim'

const PLACEHOLDERS: Partial<Record<ClaimStatus, string>> = {
  [ClaimStatus.SUBMITTED]: 'What your broker is checking, and anything you need from the customer.',
  [ClaimStatus.FILED]: 'The carrier reference, and anything they have asked for.',
  [ClaimStatus.OPEN_WITH_CARRIER]: 'Where the carrier has got to, and anything outstanding.',
  [ClaimStatus.CLOSED]: 'How it settled, and what the customer should expect next.',
}

export function ClaimStatusNotesDialog({
  claim,
  statuses,
}: {
  claim: InternalClaim
  statuses: ClaimStatus[]
}) {
  const setNote = useSetClaimStatusNote()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [drafts, setDrafts] = useState<Partial<Record<ClaimStatus, string>>>({})

  // Reopening shows what the server holds now, not what someone typed here before.
  useEffect(() => {
    if (open) setDrafts({})
  }, [open])

  const saved = (status: ClaimStatus) => claim.statusNotes[status] ?? ''
  const noteFor = (status: ClaimStatus) => drafts[status] ?? saved(status)
  const changed = statuses.filter((status) => noteFor(status) !== saved(status))

  async function save() {
    setSaving(true)
    try {
      for (const status of changed) {
        await setNote.mutateAsync({ id: claim.id, status, note: noteFor(status) })
      }
      toast.success(changed.length === 1 ? 'Saved the note.' : 'Saved the notes.')
      setOpen(false)
    } catch {
      toast.error('The notes did not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="link" size="sm" className="h-auto px-0">
          Notes
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Notes for {claim.idShort}</DialogTitle>
          <DialogDescription>
            The customer reads these on the matching step of their claim timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {statuses.map((status) => (
            <div key={status} className="flex flex-col gap-2">
              <Label htmlFor={`note-${status}`}>{CLAIM_STATUS_LABELS[status]}</Label>
              <Textarea
                id={`note-${status}`}
                rows={2}
                disabled={saving}
                value={noteFor(status)}
                placeholder={PLACEHOLDERS[status]}
                onChange={(event) => setDrafts((d) => ({ ...d, [status]: event.target.value }))}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={saving ? 'Saving…' : undefined}
            disabled={changed.length === 0}
            onClick={() => void save()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
